import * as React from "react";
import { ChevronDown, Database } from "lucide-react";
import { cn } from "@finster/ui";

import {
  SOURCE_CLIENT_LABELS,
  SourceCard,
  SourceGroupSection,
  extractSourceItemsFromCitations,
  getSafeCitationHref,
  groupItemsByDataSource,
  openCitationDialog,
  type Citation,
  type OutputLink,
  type SourceItem,
} from "../../shared/citations";
import type { ExcelAgentSource } from "../agentTimeline";

interface ExcelCitationSourcesProps {
  sources?: ExcelAgentSource[];
  citations?: unknown[];
  citedValuesByUuid?: Record<string, string>;
}

interface ExcelRetrievedSourcesProps extends ExcelCitationSourcesProps {
  defaultOpen?: boolean;
}

function normalizeCitationPool(citations: unknown[] | undefined): Citation[] {
  if (!Array.isArray(citations)) return [];
  return citations.filter(
    (citation): citation is Citation =>
      Boolean(citation && typeof citation === "object" && !Array.isArray(citation)),
  );
}

function legacySourceToShared(source: ExcelAgentSource): SourceItem {
  const client = getLegacySourceClient(source);
  const metadata = {
    dataSource: source.source,
    documentName: source.title,
    companyName: source.company,
    ticker: source.ticker,
    datePublished: source.date,
  };
  const isTable = source.id.startsWith("table:");

  if (isTable) {
    const citation: Citation = {
      client,
      citationType: "Markdown",
      citationData: source.content ?? "",
      metadata,
    };
    return {
      kind: "table",
      id: source.id,
      markdown: source.content ?? "",
      client: citation.client,
      label: source.source || "Other",
      metadata,
      citedUuids: [],
      envelope: citation,
    };
  }

  const link: OutputLink = {
    text: source.content ?? "",
    link: source.url ?? "",
    metadata,
  };
  const citation: Citation = {
    client,
    citationType: "Dictionary",
    citationData: {
      [source.url || source.id]: link,
    },
    metadata,
  };
  return {
    kind: "document",
    id: source.id,
    link,
    client: citation.client,
    envelope: citation,
    label: source.source || "Other",
    citedUuids: [],
  };
}

function getLegacySourceClient(source: ExcelAgentSource): string {
  const match = /^(?:document|table):([^:]+):/.exec(source.id);
  const candidate = match?.[1];
  if (!candidate || /^(?:https?|gcs?|file)$/i.test(candidate)) {
    return "excel_legacy";
  }
  return candidate;
}

function sourceItemSemanticKey(item: SourceItem): string {
  if (item.kind === "document") {
    const sourceUrl = item.link.link || item.link.originalPdfUrl;
    return sourceUrl
      ? `document:${item.client}:${sourceUrl}`
      : `document:${item.client}:${item.id}`;
  }
  return `table:${item.client}:${item.markdown}`;
}

export function buildExcelCitationItems({
  sources,
  citations,
}: ExcelCitationSourcesProps): {
  items: SourceItem[];
  pool: Citation[];
} {
  const pool = normalizeCitationPool(citations);
  const rawItems =
    pool.length > 0
      ? extractSourceItemsFromCitations(pool, {
      clientLabels: SOURCE_CLIENT_LABELS,
        })
      : [];

  const items: SourceItem[] = [];
  const seenIds = new Set<string>();
  const seenSemanticKeys = new Set<string>();
  const appendItem = (item: SourceItem) => {
    const semanticKey = sourceItemSemanticKey(item);
    if (seenIds.has(item.id) || seenSemanticKeys.has(semanticKey)) return;
    seenIds.add(item.id);
    seenSemanticKeys.add(semanticKey);
    items.push(item);
  };

  for (const item of rawItems) {
    appendItem(item);
  }

  for (const source of sources ?? []) {
    appendItem(legacySourceToShared(source));
  }
  return { items, pool };
}

export function resolveExcelInlineCitationSource(
  props: ExcelCitationSourcesProps,
  citationId: string,
): SourceItem | undefined {
  const { items } = buildExcelCitationItems(props);
  const explicitMatch = items.find((item) =>
    item.citedUuids.includes(citationId),
  );
  if (explicitMatch) return explicitMatch;

  const citedValue = props.citedValuesByUuid?.[citationId]
    ?.replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  if (citedValue) {
    const contentMatches = items.filter((item) => {
      const content = item.kind === "document" ? item.link.text : item.markdown;
      return content.replace(/\s+/g, " ").toLowerCase().includes(citedValue);
    });
    if (contentMatches.length === 1) return contentMatches[0];

    const legacySourceIds = new Set(
      (props.sources ?? [])
        .filter((source) =>
          source.citedSnippets?.some(
            (snippet) =>
              snippet.replace(/\s+/g, " ").trim().toLowerCase() === citedValue,
          ),
        )
        .map((source) => source.id),
    );
    const legacyMatches = items.filter((item) => legacySourceIds.has(item.id));
    if (legacyMatches.length === 1) return legacyMatches[0];
  }

  // A single legacy source is unambiguous. Never guess when several sources
  // exist because an unknown inline id could otherwise open the wrong source.
  return items.length === 1 ? items[0] : undefined;
}

const ExcelSourceGroups: React.FC<
  ExcelCitationSourcesProps & { emptyState?: React.ReactNode }
> = ({ sources, citations, citedValuesByUuid, emptyState = null }) => {
  const { items, pool } = React.useMemo(
    () => buildExcelCitationItems({ sources, citations, citedValuesByUuid }),
    [sources, citations, citedValuesByUuid],
  );
  const groups = React.useMemo(() => groupItemsByDataSource(items), [items]);
  const [popupError, setPopupError] = React.useState<{
    message: string;
    directHref: string;
  } | null>(null);
  React.useEffect(() => {
    setPopupError(null);
  }, [sources, citations, citedValuesByUuid]);

  const openSource = React.useCallback(
    async (item: SourceItem) => {
      setPopupError(null);
      const result = await openCitationDialog({
        sourceItem: item,
        citations: pool,
        allowIframeSameOrigin: false,
      });
      if (result.launched) return;
      const directHref =
        result.directHref ??
        (item.kind === "document" ? getSafeCitationHref(item.link.link) : "");
      setPopupError({
        message: result.reason ?? "The source popup could not be opened.",
        directHref,
      });
    },
    [pool],
  );

  const openInlineCitation = React.useCallback(
    async (id: string) => {
      setPopupError(null);
      const result = await openCitationDialog({
        citationId: id,
        citations: pool,
        allowIframeSameOrigin: false,
      });
      if (!result.launched) {
        setPopupError({
          message: result.reason ?? "The source popup could not be opened.",
          directHref: result.directHref ?? "",
        });
      }
    },
    [pool],
  );

  if (items.length === 0) return <>{emptyState}</>;

  return (
    <div className="flex min-w-0 flex-col gap-1">
      {popupError && (
        <div
          role="alert"
          className="mx-1 mb-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-[11px] text-amber-900"
        >
          <span>{popupError.message}</span>
          {popupError.directHref && (
            <>
              {" "}
              <a
                href={popupError.directHref}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold underline underline-offset-2"
              >
                Open source directly
              </a>
            </>
          )}
        </div>
      )}
      {groups.map((group) => (
        <SourceGroupSection key={group.label} label={group.label} items={group.items}>
          {group.items.map((item) => (
            <SourceCard
              key={item.id}
              item={item}
              citedValuesByUuid={citedValuesByUuid}
              onView={(target) => void openSource(target)}
              onCitationView={(id) => void openInlineCitation(id)}
            />
          ))}
        </SourceGroupSection>
      ))}
    </div>
  );
};

export const ExcelRetrievedSources: React.FC<ExcelRetrievedSourcesProps> = ({
  sources,
  citations,
  citedValuesByUuid,
  defaultOpen = true,
}) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const { items } = React.useMemo(
    () => buildExcelCitationItems({ sources, citations, citedValuesByUuid }),
    [sources, citations, citedValuesByUuid],
  );
  if (items.length === 0) return null;

  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full cursor-pointer items-center gap-2 rounded-md px-1.5 py-2 text-left transition-colors hover:bg-muted/40"
      >
        <span className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
          <Database className="h-4 w-4" />
        </span>
        <span className="flex-1 text-[13px] font-medium text-foreground">
          Retrieved sources ({items.length})
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            !open && "-rotate-90",
          )}
        />
      </button>
      {open && (
        <div className="px-1.5 py-2">
          <ExcelSourceGroups
            sources={sources}
            citations={citations}
            citedValuesByUuid={citedValuesByUuid}
          />
        </div>
      )}
    </div>
  );
};

export const ExcelCitationsView: React.FC<ExcelCitationSourcesProps> = ({
  sources,
  citations,
  citedValuesByUuid,
}) => (
  <ExcelSourceGroups
    sources={sources}
    citations={citations}
    citedValuesByUuid={citedValuesByUuid}
    emptyState={
      <div className="flex h-full flex-1 flex-col items-center justify-center gap-2 px-6 py-10 text-center">
        <Database className="h-6 w-6 text-muted-foreground" aria-hidden />
        <p className="text-[13px] font-medium text-foreground">No cited sources yet</p>
        <p className="max-w-[260px] text-[12px] leading-[1.5] text-muted-foreground">
          Sources show up here when the assistant cites FactSet, SEC filings,
          web search, or other retrieved material in this workbook chat.
        </p>
      </div>
    }
  />
);
